import { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext();

// In production, use relative URL (same server). In dev, use localhost:3151
const API_URL = process.env.NODE_ENV === 'production'
  ? ''
  : (process.env.REACT_APP_API_URL || 'http://localhost:3151');

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  async function verifyWithServer(user) {
    try {
      const token = await user.getIdToken();
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok || !data.allowed) {
        setIsAdmin(false);
        await signOut(auth);
        throw new Error(data.message || 'Access denied');
      }

      setIsAdmin(data.isAdmin === true);
      return true;
    } catch (error) {
      setIsAdmin(false);
      throw error;
    }
  }

  async function signup(email, password) {
    setAuthError(null);
    const result = await createUserWithEmailAndPassword(auth, email, password);
    try {
      await verifyWithServer(result.user);
      return result;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    }
  }

  async function login(email, password) {
    setAuthError(null);
    const result = await signInWithEmailAndPassword(auth, email, password);
    try {
      await verifyWithServer(result.user);
      return result;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    }
  }

  async function loginWithGoogle() {
    setAuthError(null);
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    try {
      await verifyWithServer(result.user);
      return result;
    } catch (error) {
      setAuthError(error.message);
      throw error;
    }
  }

  function logout() {
    setIsAdmin(false);
    setAuthError(null);
    return signOut(auth);
  }

  async function getToken() {
    if (currentUser) {
      return await currentUser.getIdToken();
    }
    return null;
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          await verifyWithServer(user);
          setCurrentUser(user);
        } catch (error) {
          setAuthError(error.message);
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    isAdmin,
    authError,
    signup,
    login,
    loginWithGoogle,
    logout,
    getToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
